import { instance } from "api/apiBase";
import { getApiErrorKey, getErrorMessage } from "utils/error";
import type { LockboxVendor } from "api/landlord/lockboxes/types";

export type VendorAccountAuthType = "credentials" | "oauth";

// Mirrors the backend's AUTH_TYPE_BY_VENDOR, for branching UI on credential shape
// instead of hardcoding a vendor name.
export const AUTH_TYPE_BY_VENDOR: Record<LockboxVendor, VendorAccountAuthType> =
  {
    codebox: "credentials",
    sentrilock: "credentials",
    igloohome: "oauth"
  };

export type VendorAccountMetadata = Record<string, unknown> | null;

export interface VendorAccount {
  id: number;
  vendor: LockboxVendor;
  auth_type: VendorAccountAuthType;
  name: string;
  username: string | null;
  metadata: VendorAccountMetadata;
  lockbox_count: number;
  created_at: string;
}

export interface CreateVendorAccountPayload {
  name: string;
  username: string;
  password: string;
}

const baseUrl = (vendor: LockboxVendor) =>
  `/portal/v1/external_integrations/${vendor}/accounts`;

// These endpoints answer with a bare error key, e.g. {"error":{"message":"duplicate_name"}}.
type VendorAccountErrorCode =
  | "duplicate_name"
  | "duplicate_username"
  | "validation_failed"
  | "sentrilock_connection_failed"
  | "codebox_connection_failed"
  | "account_not_found";

const ERROR_MESSAGES: Record<VendorAccountErrorCode, string> = {
  duplicate_name: "An account with this name already exists for this vendor",
  duplicate_username:
    "This login is already used by another account for this vendor",
  validation_failed: "Check the account details and try again",
  sentrilock_connection_failed:
    "SentriLock rejected these credentials. Check the username and password, then try again",
  codebox_connection_failed:
    "Codebox rejected these credentials. Check the username and password, then try again",
  account_not_found:
    "This account no longer exists. Refresh the page and try again"
};

export const getVendorAccountErrorCode = (
  error: unknown
): VendorAccountErrorCode | null => {
  const key = getApiErrorKey(error);
  return key && key in ERROR_MESSAGES ? (key as VendorAccountErrorCode) : null;
};

export const getVendorAccountErrorMessage = (error: unknown): string => {
  const code = getVendorAccountErrorCode(error);
  return code ? ERROR_MESSAGES[code] : getErrorMessage(error);
};

export const lockboxAccountsApi = {
  list: async (vendor: LockboxVendor): Promise<VendorAccount[]> => {
    const { data } = await instance.get<VendorAccount[]>(baseUrl(vendor));
    return data;
  },

  create: async (
    vendor: LockboxVendor,
    payload: CreateVendorAccountPayload
  ): Promise<VendorAccount> => {
    const { data } = await instance.post<VendorAccount>(
      baseUrl(vendor),
      payload
    );
    return data;
  },

  rename: async (
    vendor: LockboxVendor,
    id: number,
    name: string
  ): Promise<VendorAccount> => {
    const { data } = await instance.patch<VendorAccount>(
      `${baseUrl(vendor)}/${id}`,
      { name }
    );
    return data;
  },

  remove: async (vendor: LockboxVendor, id: number): Promise<void> => {
    await instance.delete(`${baseUrl(vendor)}/${id}`);
  }
};
